import { ReactNode } from "react";

import { CircleNotch, DownloadSimple, FileJs, FilePdf } from "@phosphor-icons/react";
import { buttonVariants, Card, CardContent, CardDescription, CardTitle } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { saveAs } from "file-saver";

import { usePrintCoverLetter } from "@/client/services/cover-letter";
import { useCoverLetterStore } from "@/client/stores/cover-letter";

import { CLCollapsibleSection } from "../../../CollapsibleSection";

const handleJsonExport = () => {
  const { coverLetter } = useCoverLetterStore.getState();
  const filename = `cover_letter-${coverLetter.id}.json`;
  const json = JSON.stringify(coverLetter.data, null, 2);
  saveAs(new Blob([json], { type: "application/json" }), filename);
};

const openInNewTab = (url: string) => {
  const safeUrl = `${url}?t=${Date.now()}`;
  const win = window.open(safeUrl, "_blank");
  if (win) win.focus();
};

export const CLExportSection = () => {
  const { printCoverLetter, loading } = usePrintCoverLetter();

  const handlePdfExport = async () => {
    const { coverLetter } = useCoverLetterStore.getState();
    const { url } = await printCoverLetter({ id: coverLetter.id });
    openInNewTab(url);
  };

  const headerHTML: ReactNode = (
    <div className="flex items-center gap-x-4">
      <DownloadSimple size={18} />
      <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-lg">Export</h2>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className="grid gap-y-4">
      <Card
        className={cn(
          buttonVariants({ variant: "ghost" }),
          "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
        )}
        onClick={handleJsonExport}
      >
        <FileJs size={22} />
        <CardContent className="flex-1">
          <CardTitle className="text-sm">JSON</CardTitle>
          <CardDescription className="font-normal">
            Download a JSON snapshot of your cover letter.
          </CardDescription>
        </CardContent>
      </Card>

      <Card
        className={cn(
          buttonVariants({ variant: "ghost" }),
          "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
          loading && "pointer-events-none cursor-progress opacity-75",
        )}
        onClick={handlePdfExport}
      >
        {loading ? <CircleNotch size={22} className="animate-spin" /> : <FilePdf size={22} />}
        <CardContent className="flex-1">
          <CardTitle className="text-sm">PDF</CardTitle>
          <CardDescription className="font-normal">
            Download a PDF of your cover letter.
          </CardDescription>
        </CardContent>
      </Card>
    </main>
  );

  return <CLCollapsibleSection id="cl-export" header={headerHTML} content={contentHTML} />;
};
